import type { NavSet } from '@squareup/dex-types-shared-docs';
import type { MDXRemoteSerializeResult } from 'next-mdx-remote';

// Bundled manifest — safe on client (just JSON).
import manifestJson from '../mdx/manifest.json';

interface MdxMetadata {
  title: string;
  slug: string;
  browserTitle?: string;
  eyebrow?: string;
  summary?: string;
  releaseStatus?: string;
}

interface MdxPayload {
  mdxSource: MDXRemoteSerializeResult;
  metadata: MdxMetadata;
  navSet: NavSet;
}

interface Manifest {
  version: number;
  entries: Record<string, 'mdx' | 'contentful'>;
}

const clientManifest = manifestJson as Manifest;

// Hide the server-only `require('fs')` from webpack's static analyzer so the
// client bundle doesn't try to resolve Node built-ins. `eval('require')` uses
// the ambient CommonJS `require` at runtime and is opaque to webpack. Guarded
// by a `typeof window` check so the inner code never runs in the browser.
type ServerRequire = (id: string) => unknown;
const nodeRequire: ServerRequire | null =
  typeof window === 'undefined'
    ? // eslint-disable-next-line no-eval -- deliberate webpack-opaque server-only require
      (eval('require') as ServerRequire)
    : null;

/**
 * Server-side only: reads a precompiled MDX payload from disk.
 * Returns null if the payload is missing (manifest/build skew → fall through).
 *
 * Kept async for API symmetry with fetchMdxPayloadFromCdn so the call site
 * can use a single `await` regardless of which side it's on.
 */
// eslint-disable-next-line require-await -- async for call-site symmetry
async function loadMdxPayloadFromDisk(
  slug: string
): Promise<MdxPayload | null> {
  if (!nodeRequire) {
    return null;
  }
  const fs = nodeRequire('fs') as typeof import('fs');
  const path = nodeRequire('path') as typeof import('path');

  const publicMdxDir = path.resolve(process.cwd(), 'public/docs/_mdx-payloads');
  const payloadPath = path.resolve(publicMdxDir, `${slug}.json`);

  // Defense-in-depth traversal guard. Slugs come from a trusted manifest,
  // but verify the resolved path still lives under public/docs/_mdx-payloads.
  if (!payloadPath.startsWith(publicMdxDir + path.sep)) {
    return null;
  }

  try {
    // eslint-disable-next-line unicorn/prefer-json-parse-buffer -- JSON.parse types require string in our tsconfig
    const raw = fs.readFileSync(payloadPath, 'utf8');
    return JSON.parse(raw) as MdxPayload;
  } catch {
    return null;
  }
}

/**
 * Client-side: fetches the precompiled MDX payload JSON from the CDN.
 * Returns null on any failure (network error, 404) so the caller can
 * fall through to Contentful.
 */
async function fetchMdxPayloadFromCdn(
  slug: string
): Promise<MdxPayload | null> {
  try {
    const res = await fetch(`/docs/_mdx-payloads/${slug}.json`);
    if (!res.ok) {
      return null;
    }
    return (await res.json()) as MdxPayload;
  } catch {
    return null;
  }
}

/**
 * Server-side returns the on-disk manifest (re-reads once per process);
 * on the client returns the bundled JSON imported above.
 *
 * Kept as an async function so callers don't have to care which side they
 * are on — just `await loadManifest()`.
 */
let cachedServerManifest: Manifest | null = null;
// eslint-disable-next-line require-await -- async for call-site symmetry
async function loadManifest(): Promise<Manifest> {
  if (!nodeRequire) {
    return clientManifest;
  }
  if (cachedServerManifest) {
    return cachedServerManifest;
  }
  const fs = nodeRequire('fs') as typeof import('fs');
  const path = nodeRequire('path') as typeof import('path');
  const manifestPath = path.resolve(process.cwd(), 'mdx/manifest.json');
  try {
    // eslint-disable-next-line unicorn/prefer-json-parse-buffer -- JSON.parse types require string in our tsconfig
    const raw = fs.readFileSync(manifestPath, 'utf8');
    cachedServerManifest = JSON.parse(raw) as Manifest;
  } catch {
    cachedServerManifest = clientManifest;
  }
  return cachedServerManifest;
}

function isMdxSlug(slug: string, manifest: Manifest = clientManifest): boolean {
  return manifest.entries[slug] === 'mdx';
}

export {
  loadMdxPayloadFromDisk,
  fetchMdxPayloadFromCdn,
  loadManifest,
  clientManifest,
  isMdxSlug,
};
export type { MdxPayload, MdxMetadata, Manifest };
